/**
 * `@requirePermission(scope)` — class-method decorator that registers a
 * `methodName → requiredScope` mapping into a process-local registry.
 * `configureGrpcAuth(...)` merges this registry into the
 * `requiredScopesByMethod` config so `withAuthUnaryValue` can enforce
 * per-method scopes without each call site re-passing the table.
 *
 * Per `06-library-integration § 4.3`:
 *
 *   ```ts
 *   class InvoiceService {
 *     @requirePermission("billing.invoice.create")
 *     CreateInvoice = withAuthUnaryValue("billing.InvoiceService/CreateInvoice", ...);
 *   }
 *   ```
 *
 * Implementation uses ES2022 modern decorators (TypeScript 5.0+ /
 * stage-3 proposal). The decorator runs at class-evaluation time and
 * reads `context.name` for the method name (or the property name for
 * field decorators) — that becomes the registry key. The decorator
 * does NOT modify the descriptor; it's a side-effect registration.
 *
 * If the consumer's method name is NOT the fully-qualified gRPC
 * method (`<package>.<Service>/<Method>`), use the imperative form:
 *
 *   ```ts
 *   registerRequiredScope("billing.InvoiceService/CreateInvoice", "billing.invoice.create");
 *   ```
 *
 * Conflicting registrations (same key, different scope) throw at load
 * time — that's a programmer bug. Idempotent re-registration (same
 * key + same scope) is a no-op.
 */

import { processGlobalSlot } from "../global-state";

// Held on a process-global slot (NOT a module-level Map) so the tsup
// `splitting: false` subpath bundles share ONE registry. Otherwise a
// `@requirePermission` decorator that runs at class-load in the /server bundle
// writes /server's copy, while `getRequiredScopesRegistry()` consumed via the
// root bundle's `configureGrpcAuth` reads an empty copy — the same cross-subpath
// split as the JWKS resolver (req 4f1facd4).
const registrySlot = processGlobalSlot<Map<string, string>>(
  "requiredScopesRegistry",
);

function registry(): Map<string, string> {
  let m = registrySlot.get();
  if (!m) {
    m = new Map<string, string>();
    registrySlot.set(m);
  }
  return m;
}

/**
 * Imperative form. Use when the JS method/property name does NOT match
 * the gRPC fully-qualified method name (which is the common case for
 * services exposing gRPC).
 */
export function registerRequiredScope(methodName: string, scope: string): void {
  if (!methodName) {
    throw new Error("registerRequiredScope: methodName is required");
  }
  if (!scope) {
    throw new Error("registerRequiredScope: scope is required");
  }
  const existing = registry().get(methodName);
  if (existing !== undefined && existing !== scope) {
    throw new Error(
      `registerRequiredScope: ${methodName} already registered with scope '${existing}' (cannot redeclare as '${scope}')`,
    );
  }
  registry().set(methodName, scope);
}

/**
 * Modern decorator (`@requirePermission(scope)`). Works as a method or
 * class-field decorator. The registry key is `String(context.name)` —
 * which is the JS property name. If you need the fully-qualified gRPC
 * method name (`Service/Method`), prefer `registerRequiredScope`
 * directly (the gRPC method-name format contains `/` which is not a
 * valid JS identifier).
 */
export function requirePermission(scope: string) {
  return (
    _target: unknown,
    context:
      | ClassMethodDecoratorContext
      | ClassFieldDecoratorContext
      | { name: string | symbol; kind?: string },
  ): void => {
    const name = String((context as { name: string | symbol }).name);
    registerRequiredScope(name, scope);
  };
}

/** Read-only snapshot of the registry. */
export function getRequiredScopesRegistry(): Record<string, string> {
  return Object.fromEntries(registry());
}

/** Test-only — wipe the registry between cases. */
export function resetRequiredScopesRegistryForTesting(): void {
  registry().clear();
}
